// src/config/seo.ts

export interface SEOMetadata {
    title: string;
    description: string;
    keywords: string;
    canonicalUrl?: string;
}

export interface SEOConfig {
    [key: string]: SEOMetadata;
}

export const seoConfig: SEOConfig = {
    home: {
        title: "Text Compare & Free Online File Tools - AI Powered | FileFusion",
        description: "The best free online text comparison tool with AI summary, smart sort/trim toggles, and diff checker. Also features PDF, QR, and OCR tools. 100% private & browser-based.",
        keywords: "text compare, diff checker, ai text analysis, compare two text files, online file tools, privacy focused tools, sort text lines, remove duplicates",
    },
    textCompare: {
        title: "Best Free Text Compare Tool & Diff Checker Online | FileFusion",
        description: "Compare text files side-by-side with our free online Diff Checker. Features AI analysis, sort/trim toggles, and 100% privacy. Best for code, docs, and lists.",
        keywords: "text compare, diff checker online, compare two text files, diff tool free, text difference checker, compare code, ai text analysis, secure text compare",
        canonicalUrl: '/tools/text-compare'
    },
    features: {
        title: "Features | FileFusion Digital Toolkit",
        description: "Explore FileFusion's powerful features - text & code comparison, file management, QR code generation, barcode reading, and more digital tools.",
        keywords: "digital tools features, text comparison, file management, QR code generator, barcode reader, code comparison, technology platform",
    },
    about: {
        title: "About FileFusion | Digital Tools & Technology Platform",
        description: "Learn about FileFusion - the all-in-one platform for digital tools and technology insights designed to empower professionals, developers, and tech enthusiasts.",
        keywords: "about FileFusion, digital toolkit, technology platform, company history, professional tools, technology insights, tech knowledge hub",
    },
    contact: {
        title: "Contact Us | FileFusion Support",
        description: "Get in touch with the FileFusion team. We're here to help with all your digital tool and technology needs.",
        keywords: "contact FileFusion, digital tools support, technology help, platform support",
    },
    // Add to existing seoConfig
    tools: {
        title: 'Professional Digital Tools | FileFusion',
        description: 'Access professional-grade digital tools for every project and professional workflow. Private, secure, and built for students, creators, and professionals.',
        keywords: 'engineering tools, developer utilities, professional file tools, technical processing, text compare, pdf compressor, ocr tool, secure digital tools',
        canonicalUrl: '/tools'
    },
    fileReducer: {
        title: "Free File Compressor | Reduce File Size by 80% Online | FileFusion",
        description: "⚡ Compress files instantly by up to 80% without quality loss! ✅ Free PDF & image compression ✅ No signup required ✅ Perfect for emails & uploads → Start compressing now!",
        keywords: "file compression tool, reduce file size, PDF compressor, image compression, document compression, compress files online, compress PDF, reduce PDF size, compress JPG, compress PNG, reduce image size without losing quality, resize images, shrink PDF file, reduce file size for email, free file compressor, online file reducer, compress images for web, optimize PDF size, make PDF smaller, reduce photo size, image size reducer, best PDF compressor, compress scanned PDF, file optimizer, make files smaller, resize photos online, online image compression tool, compress Excel files, compress Word documents, how to reduce file size, how to compress PDF online, how to reduce image size, how to compress files without losing quality, step by step file compression guide",
    },
    fileMergeSplit: {
        title: "Free PDF Merger & Splitter | Combine Files Online | FileFusion",
        description: "Merge & split PDFs in seconds online! ✅ Combine multiple files ✅ Split by page range ✅ No software needed ✅ Free forever → Start merging & splitting now!",
        keywords: "how to merge PDF files online free, combine multiple PDFs into one, split large PDF into smaller files, merge documents without software, divide PDF pages online, combine PDF files in order, split PDF by page range, merge Word documents to PDF online",
    },
    // New SEO configurations for additional tools
    qrcode: {
        title: "Free QR Code Generator: Custom Logo & Colors | FileFusion",
        description: "Create custom QR codes instantly with logo, colors, and frames. 100% free, high-resolution download, and no sign-up required. Best for business cards and marketing.",
        keywords: "qr code generator free, custom qr code with logo, create qr code online, high quality qr code, wifi qr code generator, qr code maker for business card",
        canonicalUrl: '/tools/qrcode'
    },
    barcode: {
        title: "Free Online Barcode Scanner & Reader | FileFusion",
        description: "Scan barcodes and QR codes directly from your browser. Supports UPC, EAN, Code 128, and more. Instant decoding with no app installation needed.",
        keywords: "barcode scanner online, free barcode reader, scan barcode from image, upc scanner, ean scanner, browser barcode reader, qr code scanner online",
        canonicalUrl: '/tools/barcode'
    },
    insights: {
        title: "Technical Insights & Engineering Guides | FileFusion Insights",
        description: "Your hub for technical expertise. Explore in-depth engineering guides on diff algorithms and file structures, alongside comprehensive programming courses for developers.",
        keywords: "engineering guides, professional courses, programming courses, tech insights, diff algorithms, software engineering, digital innovation",
    },
    courses: {
        title: "Coding & Programming Courses | FileFusion Insights",
        description: "Master software engineering with FileFusion's Technical Learning & Development paths. Explore industrial-grade coding & programming courses on Python, JavaScript, HTML5, and C.",
        keywords: "coding courses, programming courses, learn programming, Python courses, HTML guide, JavaScript async await, C pointers explained, FileFusion courses, technical learning and development",
        canonicalUrl: '/insights/courses'
    },
    articles: {
        title: "Technical Articles & Research | FileFusion Insights",
        description: "Explore expert technical articles on diff algorithms (Myers, Patience), PDF architecture, OCR optimization, and image compression. Master digital file management with FileFusion.",
        keywords: "tech articles, technology guides, diff algorithms, myers vs patience diff, pdf architecture, ocr technology, file management, engineering guides",
    },
    ocr: {
        title: 'Free OCR Tool | Image to Text Converter Online | FileFusion',
        description: 'Extract text from images, scanned PDFs, and screenshots instantly. AI-powered OCR with high accuracy. Supports multi-language and handwriting recognition.',
        keywords: 'ocr online free, image to text converter, extract text from image, pdf to text, picture to text, online ocr scanner, text extractor from image',
        canonicalUrl: '/tools/ocr'
    },
    backgroundRemover: {
        title: 'Free Background Remover | Remove BG from Image Online | FileFusion',
        description: 'Remove image backgrounds automatically in 1 click. Create transparent backgrounds for products, logos, and portraits. AI-powered and 100% free.',
        keywords: 'remove background online, free background remover, transparent background maker, remove bg free, photo background eraser, auto background remover',
        canonicalUrl: '/tools/background-remover'
    },
    faq: {
        title: "Frequently Asked Questions | FileFusion Help Center",
        description: "Find answers to common questions about FileFusion's online tools, features, privacy practices, and more in our comprehensive FAQ section.",
        keywords: "FileFusion FAQ, file tools help, digital tools FAQ, file conversion help, text comparison help, QR code help",
        canonicalUrl: '/faq'
    },
    jsonValidator: {
        title: "Free JSON Validator | Beautify & Fix JSON Online | FileFusion",
        description: "Validate, format, and fix JSON data instantly. Features syntax highlighting, error detection, and auto-repair. The best free JSON tool for developers.",
        keywords: 'JSON validator, JSON formatter, JSON beautifier, JSON linter, fix JSON online, JSON parser, validate JSON file, debug JSON',
        canonicalUrl: '/tools/json-validator'
    },
    devHub: {
        title: "Professional Developer Services | FileFusion",
        description: "Professional-grade infrastructure services for high-performance engineering. Test your apps with our industrial-grade Mock API, Webhook Debugger, and Email Sandbox.",
        keywords: "developer services, mock api sandbox, webhook tester online, email sandbox dev tool, payment simulator test, devhub filefusion, web debugger, engineering tools, chaos engineering api",
        canonicalUrl: '/dev-hub'
    }
};
